import * as React from 'react';
import styled from 'styled-components';
import {useFormikContext} from 'formik';
import {CourseObject} from 'api/course';
import CourseBuilderContext from 'components/CourseBuilder/CourseBuilderContext';
import Row from 'components/Row';
import Column from 'components/Column';
import Input from 'components/Input';
import SelectPrice, {SelectPriceProps} from 'components/SelectPrice';
import TotalPrice from 'components/TotalPrice';
import Button, {
  ButtonScale,
  ButtonVariant,
  Container as ButtonContainer,
} from 'components/Button';
import {OptionObject} from 'components/Select';
import FormContainer from '../../containers/FormContainer';
import {PriceObject, PromocodeObject, PromocodeType} from 'types';
import {IconTrash, IconRoundPlus} from 'icons';
import {DatePicker} from "components/DatePicker";
import {DatePickerDateArg} from "components/DatePicker/DatePicker";

const Promocodes = styled.div`
  row-gap: 10px;
  display: grid;
`;

const Promocode = styled.div`
  &:not(:last-child) {
    padding-bottom: 10px;
    border-bottom: 1px solid ${({theme}) => `
      ${theme.variables.borderColor};
    `};
  }
`;

const PromocodeContainer = styled.div`
  position: relative;
  padding-right: 55px;
`;

const DeleteContainer = styled.div`
  position: absolute;
  top: 38px;
  right: 10px;
  bottom: 0;
  
  ${ButtonContainer} {
    max-height: 128px;
    height: 100%;
  }
`;

/**
 * The promocodes form part of settings
 * @return {React.ReactElement}
 * @constructor
 */
export default function PromocodesForm(): React.ReactElement {
  const formContext = useFormikContext<Partial<CourseObject>>();
  const courseContext = React.useContext(CourseBuilderContext);

  /**
   * Render the content
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    /**
     * Render promocodes field
     * @return {JSX.Element}
     */
    function renderPromocodes(): JSX.Element {
      /**
       * Render the promocode
       * @param {PromocodeObject} promocode
       * @param {number} index
       * @return {JSX.Element}
       */
      function renderPromocode(
          promocode: PromocodeObject,
          index: number,
      ): JSX.Element {
        /**
         * Get the field
         * @param {string} field
         * @return {any}
         */
        function getField(field: keyof PromocodeObject) {
          return formContext.values.promocodes?.list[index][field];
        }

        /**
         * Set the promocode field
         * @param {string} field
         * @param {T} value
         */
        function setField<T extends any>(
            field: keyof PromocodeObject,
            value: T,
        ): void {
          formContext.setFieldValue(
              `promocodes.list.${index}.${field}`,
              value,
          );
          formContext.submitForm();
        }

        /**
         * Render code field
         * @return {JSX.Element}
         */
        function renderCode(): JSX.Element {
          /**
           * Handle input blur
           * @param {React.FocusEvent<HTMLInputElement>} event
           */
          function handleBlur(event: React.FocusEvent<HTMLInputElement>): void {
            const code = event.target.value.length > 0 ? (
              event.target.value
            ) : undefined;

            setField('code', code);
          }

          return (
            <Row>
              <Column>
                <Input
                  type="text"
                  label="Кодовое слово"
                  placeholder="Введите кодовое слово"
                  defaultValue={getField('code') as string | undefined}
                  onBlur={handleBlur}
                  required
                />
              </Column>
            </Row>
          );
        }

        /**
         * Render dates
         * @return {JSX.Element}
         */
        function renderDates(): JSX.Element {
          const dates = getField('dates') as PromocodeObject['dates'] | undefined;

          /**
           * Render start date field
           * @return {JSX.Element}
           */
          function renderStartDate(): JSX.Element {
            /**
             * Handle date change
             * @param {DatePickerDateArg} date
             */
            function handleChange(date: DatePickerDateArg): void {
              setField('dates', {
                ...dates,
                startDate: date,
              });
            }

            return (
              <Column>
                <DatePicker
                  id={`promocodes.${index}.dates.startDate`}
                  label="Дата старта"
                  dateFormat="dd.MM.yyyy в HH:mm"
                  selected={dates?.startDate}
                  showTimeSelect
                  onChange={handleChange}
                  required
                />
              </Column>
            );
          }

          /**
           * Render over date field
           * @return {JSX.Element}
           */
          function renderOverDate(): JSX.Element {
            /**
             * Handle date change
             * @param {DatePickerDateArg} date
             */
            function handleChange(date: DatePickerDateArg): void {
              if (date === null) {
                setField('dates', {
                  ...dates,
                  overDate: undefined,
                });
                return;
              }

              setField('dates', {
                ...dates,
                overDate: date,
              });
            }

            return (
              <Column>
                <DatePicker
                  id={`promocodes.${index}.dates.overDate`}
                  label="Дата окончания"
                  dateFormat="dd.MM.yyyy в HH:mm"
                  selected={dates?.overDate}
                  showTimeSelect
                  onChange={handleChange}
                />
              </Column>
            );
          }

          return (
            <Row>
              {renderStartDate()}
              {renderOverDate()}
            </Row>
          );
        }

        /**
         * Render discount field
         * @return {JSX.Element}
         */
        function renderDiscount(): JSX.Element {
          const options: OptionObject[] = [
            {label: 'Число', value: PromocodeType.Fixed},
            // {label: 'Процент', value: PromocodeType.Percent},
          ];

          /**
           * Get the default option
           * @return {OptionObject | undefined}
           */
          function getDefaultOption(): OptionObject | undefined {
            return options.find((option) => (
              option.value === getField('type') as String | undefined
            ));
          }

          /**
           * Handle discount input blur
           * @param {React.ChangeEvent<HTMLInputElement>} event
           */
          function handleDiscountBlur(
              event: React.ChangeEvent<HTMLInputElement>,
          ): void {
            const discount = Number(event.target.value) > 0 ? (
              Number(event.target.value)
            ) : undefined;

            setField('discount', discount);
          }

          /**
           * handle type change
           * @param {OptionObject} option
           */
          function handleTypeChange(option: OptionObject | any): void {
            setField('type', option.value);
          }

          const inputProps: SelectPriceProps['inputProps'] = {
            placeholder: 'Укажите скидку',
            defaultValue: getField('discount') as number | undefined,
            onBlur: handleDiscountBlur,
          };

          const selectProps: SelectPriceProps['selectProps'] = {
            placeholder: 'Тип',
            defaultValue: getDefaultOption(),
            onChange: handleTypeChange,
          };

          /**
           * Handle discount change
           * @param {Partial<PriceObject>} price
           */
          function handleChange(price: Partial<PriceObject>): void {}

          return (
            <Row>
              <Column>
                <SelectPrice
                  label="Скидка"
                  inputProps={inputProps}
                  selectProps={selectProps}
                  options={options}
                  onChange={handleChange}
                  required
                />
              </Column>
            </Row>
          );
        }

        /**
         * Render limit field
         * @return {JSX.Element}
         */
        function renderLimit(): JSX.Element {
          /**
           * Handle input blur
           * @param {React.FocusEvent<HTMLInputElement>} event
           */
          function handleBlur(event: React.FocusEvent<HTMLInputElement>): void {
            const limits = Number(event.target.value) > 0 ? (
              Number(event.target.value)
            ) : undefined;

            setField('limits', limits);
          }

          return (
            <Row>
              <Column>
                <Input
                  type="number"
                  label="Ограничение (количество человек)"
                  note="Оставьте поле пустым, если без ограничений"
                  placeholder="Без ограничений"
                  defaultValue={getField('limits') as number | undefined}
                  onBlur={handleBlur}
                />
              </Column>
            </Row>
          );
        }

        /**
         * Render total price
         * @return {JSX.Element}
         */
        function renderTotalPrice(): JSX.Element {
          return (
            <Row>
              <Column>
                <Row>
                  <Column>
                    {(
                      courseContext.course?.price?.amount
                      && courseContext.course?.price?.currency
                    ) && (
                      <TotalPrice
                        sale={getField('discount') as number | undefined}
                        price={courseContext.course?.price as PriceObject}
                      />
                    )}
                  </Column>
                </Row>
              </Column>
            </Row>
          );
        }

        /**
         * Render delete button
         * @return {JSX.Element}
         */
        function renderDeleteButton(): JSX.Element {
          /**
           * Handle click on delete button
           */
          function handleClick(): void {
            console.log('delete the promocode');
          }

          return (
            <DeleteContainer>
              <Button
                type="button"
                scale={ButtonScale.Small}
                onClick={handleClick}
              >
                <IconTrash />
              </Button>
            </DeleteContainer>
          );
        }

        return (
          <Promocode>
            <PromocodeContainer>
              <Row>
                <Column>
                  {renderCode()}
                  {renderDates()}
                </Column>
                <Column>
                  {renderDiscount()}
                  {renderLimit()}
                </Column>
              </Row>
              {renderDeleteButton()}
            </PromocodeContainer>
            {renderTotalPrice()}
          </Promocode>
        );
      }

      return (
        <Promocodes>
          {formContext.values.promocodes?.list?.map((promocode, index) => (
            renderPromocode(promocode, index)
          ))}
        </Promocodes>
      );
    }

    /**
     * Render push button
     * @return {JSX.Element}
     */
    function renderPushButton(): JSX.Element {
      /**
       * Create the promocode
       * @return {PromocodeObject}
       */
      function createPromocode(): Partial<PromocodeObject> {
        return {type: PromocodeType.Fixed};
      }

      /**
       * Handle click on the button
       */
      function handleClick(): void {
        formContext.setFieldValue('promocodes.list', formContext.values.promocodes?.list ? [
          ...formContext.values.promocodes?.list,
          createPromocode(),
        ] : [createPromocode()]);
        formContext.submitForm();
      }

      return (
        <Row>
          <Column>
            <Row>
              <Column>
                <Button
                  type="button"
                  variant={ButtonVariant.Outline}
                  onClick={handleClick}
                  stretched
                >
                  <IconRoundPlus />
                  Добавить промокод
                </Button>
              </Column>
            </Row>
          </Column>
        </Row>
      );
    }

    return (
      <React.Fragment>
        {renderPromocodes()}
        {renderPushButton()}
      </React.Fragment>
    );
  }

  return (
    <FormContainer
      property="promocodes"
      label="Промокоды"
      collapse
      notAvailableForFree
    >
      {renderContent()}
    </FormContainer>
  );
}
